import type {
  BusinessOpportunity,
  BusinessOpportunityStatus,
  OpportunityFeedback,
  OpportunityFeedbackSentiment,
  OpportunityIteration,
  OpportunityIterationTrigger,
  OpportunityRefinement,
} from '../types';
import { normalizeDeckIntelligence } from './deckIntelligence';

const STATUSES: BusinessOpportunityStatus[] = ['draft', 'refining', 'validated', 'archived'];
const FEEDBACK_SENTIMENTS: OpportunityFeedbackSentiment[] = ['positive', 'neutral', 'negative'];

export function businessOpportunityStatusLabel(status: BusinessOpportunityStatus): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'refining':
      return 'Refining';
    case 'validated':
      return 'Validated';
    case 'archived':
      return 'Archived';
    default:
      return status;
  }
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function normalizeRefinement(raw: unknown): OpportunityRefinement | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = asString(row.id)?.trim();
  const createdAt = asString(row.createdAt);
  const content = asString(row.content)?.trim();
  const type = row.type === 'user' || row.type === 'ai' ? row.type : null;
  if (!id || !createdAt || !content || !type) return null;
  return { id, createdAt, type, content };
}

const ITERATION_TRIGGERS: OpportunityIterationTrigger[] = ['analyze', 'refine', 'resolution'];

function normalizeIteration(raw: unknown): OpportunityIteration | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = asString(row.id)?.trim();
  const createdAt = asString(row.createdAt);
  const summary = asString(row.summary)?.trim();
  const trigger = ITERATION_TRIGGERS.includes(row.trigger as OpportunityIterationTrigger)
    ? (row.trigger as OpportunityIterationTrigger)
    : null;
  const overallScore =
    typeof row.overallScore === 'number' && Number.isFinite(row.overallScore)
      ? row.overallScore
      : null;
  if (!id || !createdAt || !summary || !trigger || overallScore == null) return null;

  const scoresRaw = row.sectionScores;
  let sectionScores: OpportunityIteration['sectionScores'];
  if (scoresRaw && typeof scoresRaw === 'object') {
    const scores = scoresRaw as Record<string, unknown>;
    const keys = ['team', 'market', 'product', 'businessModel', 'traction', 'funding'] as const;
    if (keys.every((key) => typeof scores[key] === 'number' && Number.isFinite(scores[key]))) {
      sectionScores = {
        team: scores.team as number,
        market: scores.market as number,
        product: scores.product as number,
        businessModel: scores.businessModel as number,
        traction: scores.traction as number,
        funding: scores.funding as number,
      };
    }
  }

  return { id, createdAt, trigger, summary, overallScore, sectionScores };
}

function normalizeFeedback(raw: unknown): OpportunityFeedback | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = asString(row.id)?.trim();
  const createdAt = asString(row.createdAt);
  const source = asString(row.source)?.trim();
  const quote = asString(row.quote)?.trim();
  if (!id || !createdAt || !source || !quote) return null;
  const sentimentRaw = asString(row.sentiment);
  const sentiment =
    sentimentRaw && FEEDBACK_SENTIMENTS.includes(sentimentRaw as OpportunityFeedbackSentiment)
      ? (sentimentRaw as OpportunityFeedbackSentiment)
      : undefined;
  return { id, createdAt, source, quote, sentiment };
}

function normalizePnlMonth(raw: unknown): import('../types').OpportunityPnlMonth | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const month = typeof row.month === 'number' && Number.isFinite(row.month) ? row.month : null;
  const readAmount = (key: string): number | null => {
    const value = row[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  };
  const revenue = readAmount('revenue');
  const cogs = readAmount('cogs');
  const personnel = readAmount('personnel');
  const marketing = readAmount('marketing');
  const researchDevelopment = readAmount('researchDevelopment');
  const generalAdmin = readAmount('generalAdmin');
  const otherOpex = readAmount('otherOpex');
  const depreciation = readAmount('depreciation');
  const interestExpense = readAmount('interestExpense');
  const taxes = readAmount('taxes');
  if (
    month == null ||
    revenue == null ||
    cogs == null ||
    personnel == null ||
    marketing == null ||
    researchDevelopment == null ||
    generalAdmin == null ||
    otherOpex == null ||
    depreciation == null ||
    interestExpense == null ||
    taxes == null
  ) {
    return null;
  }
  return {
    month,
    revenue,
    cogs,
    personnel,
    marketing,
    researchDevelopment,
    generalAdmin,
    otherOpex,
    depreciation,
    interestExpense,
    taxes,
  };
}

function normalizePnlYearPlan(raw: unknown): import('../types').OpportunityPnlYearPlan | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const year = typeof row.year === 'number' && Number.isFinite(row.year) ? row.year : null;
  const months = Array.isArray(row.months)
    ? row.months.map(normalizePnlMonth).filter((m): m is NonNullable<typeof m> => m !== null)
    : [];
  if (year == null || months.length !== 12) return null;
  return { year, months };
}

function normalizePnlYear(raw: unknown): import('../types').OpportunityPnlYear | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const year = typeof row.year === 'number' && Number.isFinite(row.year) ? row.year : null;
  const revenue = typeof row.revenue === 'number' && Number.isFinite(row.revenue) ? row.revenue : null;
  const cogs = typeof row.cogs === 'number' && Number.isFinite(row.cogs) ? row.cogs : null;
  const grossProfit =
    typeof row.grossProfit === 'number' && Number.isFinite(row.grossProfit) ? row.grossProfit : null;
  const opex = typeof row.opex === 'number' && Number.isFinite(row.opex) ? row.opex : null;
  const ebitda = typeof row.ebitda === 'number' && Number.isFinite(row.ebitda) ? row.ebitda : null;
  if (year == null || revenue == null || cogs == null || grossProfit == null || opex == null || ebitda == null) {
    return null;
  }
  return { year, revenue, cogs, grossProfit, opex, ebitda };
}

function normalizePnl(raw: unknown): BusinessOpportunity['pnl'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  const generatedAt = asString(row.generatedAt);
  const model = asString(row.model);
  const summary = asString(row.summary);
  const currency = row.currency === 'EUR' ? 'EUR' : null;
  if (!generatedAt || !model || !summary || !currency) return undefined;

  const yearPlans = Array.isArray(row.yearPlans)
    ? row.yearPlans.map(normalizePnlYearPlan).filter((y): y is NonNullable<typeof y> => y !== null)
    : [];

  const legacyYears = Array.isArray(row.years)
    ? row.years.map(normalizePnlYear).filter((y): y is NonNullable<typeof y> => y !== null)
    : [];

  if (yearPlans.length === 0 && legacyYears.length === 0) return undefined;

  const keyAssumptions = Array.isArray(row.keyAssumptions)
    ? row.keyAssumptions.filter((x): x is string => typeof x === 'string')
    : [];
  const revenueDrivers = Array.isArray(row.revenueDrivers)
    ? row.revenueDrivers.filter((x): x is string => typeof x === 'string')
    : [];
  const costDrivers = Array.isArray(row.costDrivers)
    ? row.costDrivers.filter((x): x is string => typeof x === 'string')
    : [];

  const breakEvenMonth =
    typeof row.breakEvenMonth === 'number' && Number.isFinite(row.breakEvenMonth)
      ? row.breakEvenMonth
      : undefined;

  return {
    generatedAt,
    model,
    currency,
    summary,
    yearPlans,
    ...(legacyYears.length ? { years: legacyYears } : {}),
    ...(breakEvenMonth !== undefined ? { breakEvenMonth } : {}),
    keyAssumptions,
    revenueDrivers,
    costDrivers,
  };
}

const ROADMAP_PHASES: import('../types').OpportunityRoadmapPhase[] = ['now', 'next', 'later'];
const ROADMAP_LEVELS: import('../types').OpportunityRoadmapLevel[] = ['high', 'medium', 'low'];

function normalizeRoadmapItem(raw: unknown): import('../types').OpportunityRoadmapItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = asString(row.id)?.trim();
  const title = asString(row.title)?.trim();
  const description = asString(row.description)?.trim();
  const phase = ROADMAP_PHASES.includes(row.phase as import('../types').OpportunityRoadmapPhase)
    ? (row.phase as import('../types').OpportunityRoadmapPhase)
    : null;
  const impact = ROADMAP_LEVELS.includes(row.impact as import('../types').OpportunityRoadmapLevel)
    ? (row.impact as import('../types').OpportunityRoadmapLevel)
    : null;
  const effort = ROADMAP_LEVELS.includes(row.effort as import('../types').OpportunityRoadmapLevel)
    ? (row.effort as import('../types').OpportunityRoadmapLevel)
    : null;
  if (!id || !title || !description || !phase || !impact || !effort) return null;
  return {
    id,
    phase,
    title,
    description,
    impact,
    effort,
    timeframe: asString(row.timeframe)?.trim() || undefined,
    done: row.done === true ? true : undefined,
  };
}

function normalizeRoadmap(raw: unknown): BusinessOpportunity['roadmap'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  const generatedAt = asString(row.generatedAt);
  const model = asString(row.model);
  const summary = asString(row.summary);
  if (!generatedAt || !model || summary === undefined) return undefined;
  const items = Array.isArray(row.items)
    ? row.items.map(normalizeRoadmapItem).filter((i): i is NonNullable<typeof i> => i !== null)
    : [];
  if (items.length === 0) return undefined;
  return {
    generatedAt,
    model,
    summary,
    items,
    editedAt: asString(row.editedAt) || undefined,
  };
}

export function normalizeBusinessOpportunity(raw: Record<string, unknown>, docId: string): BusinessOpportunity {
  const now = new Date().toISOString();
  const id =
    typeof raw.id === 'string' && raw.id.trim()
      ? raw.id.trim()
      : docId.split('__').slice(1).join('__') || docId;
  const name = typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : 'Untitled idea';
  const ideaSummary = typeof raw.ideaSummary === 'string' ? raw.ideaSummary : '';
  const statusRaw = asString(raw.status);
  const status = statusRaw && STATUSES.includes(statusRaw as BusinessOpportunityStatus)
    ? (statusRaw as BusinessOpportunityStatus)
    : 'draft';
  const createdAt = asString(raw.createdAt) ?? now;
  const updatedAt = asString(raw.updatedAt) ?? createdAt;

  const refinements = Array.isArray(raw.refinements)
    ? raw.refinements.map(normalizeRefinement).filter((r): r is OpportunityRefinement => r !== null)
    : undefined;
  const iterations = Array.isArray(raw.iterations)
    ? raw.iterations.map(normalizeIteration).filter((i): i is OpportunityIteration => i !== null)
    : undefined;
  const feedback = Array.isArray(raw.feedback)
    ? raw.feedback.map(normalizeFeedback).filter((f): f is OpportunityFeedback => f !== null)
    : undefined;

  return {
    id,
    name,
    industry: asString(raw.industry)?.trim() || undefined,
    ideaSummary,
    status,
    ideaIntelligence: normalizeDeckIntelligence(raw.ideaIntelligence),
    refinements: refinements?.length ? refinements : undefined,
    iterations: iterations?.length ? iterations : undefined,
    feedback: feedback?.length ? feedback : undefined,
    pnl: normalizePnl(raw.pnl),
    roadmap: normalizeRoadmap(raw.roadmap),
    promotedCaseId: asString(raw.promotedCaseId)?.trim() || undefined,
    createdAt,
    updatedAt,
  };
}

/** Compile forged idea into deck OCR text for dealflow promotion. */
export function compileOpportunityBrief(opportunity: BusinessOpportunity): string {
  const lines: string[] = [
    `# ${opportunity.name}`,
    '',
    '## Idea summary',
    opportunity.ideaSummary.trim(),
  ];

  if (opportunity.industry?.trim()) {
    lines.push('', `Industry: ${opportunity.industry.trim()}`);
  }

  const intel = opportunity.ideaIntelligence;
  if (intel) {
    lines.push('', '## Analysis snapshot', '', `### Market`, intel.market, '', `### Product`, intel.product, '', `### Business model`, intel.businessModel);
    if (intel.analysis.mostDangerousAssumption) {
      lines.push('', `Most dangerous assumption: ${intel.analysis.mostDangerousAssumption}`);
    }
  }

  if (opportunity.refinements?.length) {
    lines.push('', '## Refinements');
    for (const r of opportunity.refinements) {
      lines.push(`- [${r.type}] ${r.content}`);
    }
  }

  if (opportunity.feedback?.length) {
    lines.push('', '## Early feedback');
    for (const f of opportunity.feedback) {
      lines.push(`- ${f.source}: "${f.quote}"${f.sentiment ? ` (${f.sentiment})` : ''}`);
    }
  }

  if (opportunity.pnl) {
    lines.push('', '## P&L summary', opportunity.pnl.summary);
    for (const plan of opportunity.pnl.yearPlans ?? []) {
      const revenue = plan.months.reduce((sum, m) => sum + m.revenue, 0);
      const ebitda = plan.months.reduce((sum, m) => {
        const gross = m.revenue - m.cogs;
        const opex =
          m.personnel + m.marketing + m.researchDevelopment + m.generalAdmin + m.otherOpex;
        return sum + (gross - opex);
      }, 0);
      lines.push(`Year ${plan.year}: revenue €${revenue.toFixed(0)}, EBITDA €${ebitda.toFixed(0)}`);
    }
  }

  return lines.join('\n').trim();
}
